import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  IconButton,
  Alert,
  TextField,
  Card,
  CardContent,
  CardActions,
  Collapse,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { fetchPreloadedImages, generateAdImage } from '../../utils/openaiUtils';
import ImageEditDialog from './ImageEditDialog';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 800,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

const ImageSelector = ({ open, onClose, slug, onSelectImage }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingNew, setGeneratingNew] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  
  // Image editing state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [imageToEdit, setImageToEdit] = useState(null);

  // Generate default prompt based on slug
  useEffect(() => {
    if (slug) {
      const title = slug.split('-').join(' ');
      const defaultPrompt = `
Create a scroll-stopping 1080×1080 Facebook ad image for the campaign titled “${title}”.

TEXT SYNTHESIS RULE:
• From the supplied campaign title, keep only the essential keywords (3-6 words).
• Remove generic filler phrases such as “what you need to know”, “everything you need to know”, “complete guide”, “tips”, “info”, “best”, “ultimate”, etc.
• Use the distilled phrase as the MAIN HEADLINE in the image.

VISUAL STYLE (native-ad / health-blog look):
• One clear photo or simple illustration that directly represents the topic.
• Overlay two solid-color rectangular banners in bright, high-contrast hues:
    – TOP banner (≈12 % height): small caption “Read More About”.
    – MAIN banner just below (≈12 % height): **bold ALL-CAPS headline** (the distilled phrase).
• Optional third banner at bottom (≤10 % height) for an extra 1-to-3-word support phrase if needed.
• Flat design, no gradients or fancy effects.

IMPORTANT FACEBOOK REQUIREMENTS:
- Include **exactly one** high-contrast “Learn More” button; no other buttons.
- All overlay text combined must cover **less than 25 %** of the image area.
- Only one short call-to-action (1–4 words) plus up to two tiny support words; no paragraphs.
- Keep layout clean, modern and highly legible.
- Avoid any controversial, sensitive or disallowed imagery.

Return **ONLY** the visual description; do NOT include any code, commentary or labels.
`.trim();    setCustomPrompt(defaultPrompt);
    }
  }, [slug]);

  // Load images when component mounts or slug changes
  useEffect(() => {
    if (open && slug) {
      loadImages();
    }
  }, [open, slug]);

  // Load existing images
  const loadImages = async () => {
    setLoading(true);
    setError(null);
    console.log('Loading images for slug:', slug);
    try {
      console.log('Calling fetchPreloadedImages...');
      const preloadedImages = await fetchPreloadedImages(slug);
      console.log('Received images from API:', preloadedImages);
      setImages(preloadedImages);
    } catch (error) {
      console.error('Error loading images:', error);
      setError('Error loading existing images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate a new image
  const handleGenerateNew = async () => {
    if (!slug) return;
    
    setGeneratingNew(true);
    setError(null);
    try {
      // Prepare parameters for image generation
      const params = {
        page: {
          title: slug.split('-').join(' '),
          slug: slug
        },
        style: 'professional'
      };
      
      // Add custom prompt if enabled
      if (useCustomPrompt && customPrompt.trim()) {
        params.customPrompt = customPrompt.trim();
      }
      
      // Generate the image
      const newImage = await generateAdImage(params);
      
      // Add the new image to the list
      setImages(prev => [...prev, newImage]);
      
    } catch (error) {
      console.error('Error generating new image:', error);
      setError('Failed to generate image: ' + (error.message || 'OpenAI safety system may have rejected the request'));
    } finally {
      setGeneratingNew(false);
    }
  };

  // Handle opening edit dialog
  const handleEditImage = (image, event) => {
    // Prevent triggering the select image event
    event.stopPropagation();
    setImageToEdit(image);
    setEditDialogOpen(true);
  };

  // Handle image edited result
  const handleImageEdited = (editedImageUrl) => {
    // Create a new image object with the edited URL
    const editedImage = {
      url: editedImageUrl,
      isEdited: true
    };
    
    // Add the edited image to the list
    setImages(prev => [...prev, editedImage]);
    
    // Optionally auto-select the edited image
    onSelectImage(editedImage);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="image-selector-modal"
    >
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Select an Image</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Advanced prompt settings */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Image Generation</Typography>
              <Button 
                endIcon={<ExpandMoreIcon sx={{ 
                  transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}/>}
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
              </Button>
            </Box>
          </CardContent>
          
          <Collapse in={showAdvanced}>
            <CardContent>
              <FormControlLabel 
                control={
                  <Switch 
                    checked={useCustomPrompt} 
                    onChange={(e) => setUseCustomPrompt(e.target.checked)}
                  />
                } 
                label="Use custom prompt" 
                sx={{ mb: 2 }}
              />
              
              <TextField
                label="Custom Prompt"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                fullWidth
                multiline
                rows={8}
                disabled={!useCustomPrompt}
                variant="outlined"
                sx={{ mb: 2 }}
              />
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Customize the prompt used to generate the image. Be specific about the style, content, and composition you want.
              </Typography>
              
              <Button 
                variant="outlined" 
                color="primary"
                onClick={() => {
                  const title = slug.split('-').join(' ');
                  const defaultPrompt = `Create a professional, scroll-stopping Facebook ad image (1080×1080) for an ad titled '${title}'.\nVisual style: professional.\nIMPORTANT REQUIREMENTS (Facebook ads):\n- Show always 'learn more button', no other button is allowed. Should always be in high contrast with background image.\n- Overlay text must occupy LESS THAN 25% of the image area.\n- Use only one short call-to-action (1–4 words) plus up to 2 tiny support words; no paragraphs.\n- Keep design clean and modern with high contrast.\n- Avoid controversial, sensitive or disallowed imagery.\nReturn ONLY the visual description; do NOT include any additional commentary or labels.`;
                  setCustomPrompt(defaultPrompt);
                }}
                size="small"
                startIcon={<EditIcon />}
              >
                Reset to Default Prompt
              </Button>
            </CardContent>
          </Collapse>
          
          <CardActions>
            <Button 
              onClick={handleGenerateNew}
              variant="contained"
              color="primary"
              startIcon={generatingNew ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              disabled={generatingNew}
              fullWidth
            >
              {generatingNew ? 'Generating...' : 'Generate New Image'}
            </Button>
          </CardActions>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Available Images
            </Typography>
            
            <Box sx={{ 
              maxHeight: '400px',
              overflowY: 'auto',
              pr: 1 // Add a bit of padding on the right to accommodate the scrollbar
            }}>
              <Grid container spacing={2}>
                {images.map((image, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Box sx={{ position: 'relative' }}>
                      <Paper 
                        onClick={() => onSelectImage(image)}
                        sx={{ 
                          cursor: 'pointer', 
                          p: 1,
                          '&:hover': {
                            boxShadow: 3,
                            transform: 'scale(1.02)',
                            transition: 'all 0.2s'
                          }
                        }}
                      >
                        <img 
                          src={image.url} 
                          alt={`Option ${index + 1}`} 
                          style={{ width: '100%', height: 'auto', borderRadius: 4 }}
                        />
                        
                        {/* Edit button overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1
                          }}
                        >
                          <Tooltip title="Edit this image with AI">
                            <IconButton
                              size="small"
                              onClick={(e) => handleEditImage(image, e)}
                              sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                boxShadow: 1,
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 1)',
                                  boxShadow: 2
                                }
                              }}
                            >
                              <AutoFixHighIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        
                        {/* Edited indicator */}
                        {image.isEdited && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 8,
                              left: 8,
                              backgroundColor: 'success.main',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              fontWeight: 'bold'
                            }}
                          >
                            Edited
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
            
            {images.length === 0 && !loading && (
              <Box sx={{ mt: 2, mb: 2, textAlign: 'center' }}>
                <Typography variant="subtitle1" color="text.primary" gutterBottom>
                  No images found for this page
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use the button above to generate your first image for this ad.
                </Typography>
              </Box>
            )}
          </>
        )}
        
        {/* Image Edit Dialog */}
        <ImageEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          imageUrl={imageToEdit?.url}
          onImageEdited={handleImageEdited}
        />
      </Box>
    </Modal>
  );
};

export default ImageSelector; 